import React, { useEffect, useState, useRef } from 'react'
import { abortUpload, UploadTask } from '@Utils/S3Storage'
import classnames from 'classnames'
import resizeImage from '@Utils/ResizeImage'
import AvatarUploader, { IUploadedAvatar } from '@Utils/AvatarUploader'
import S3Remover from '@Utils/S3Remover'
import Pen from '@Images/edit_customization_item.svg'

import {
  IDraftSpeakerVoice,
} from '@SiteModules/SpeakerVoice/types'

import './SpeakerVoiceThumbnail.scss'

export interface IFileToUpload {
  readonly file: File
  readonly hasStartedUpload: boolean
  readonly hasCompletedUpload: boolean
  readonly uploadingRequest?: UploadTask
  readonly url?: string
}

interface SpeakerVoiceThumbnailProps {
  readonly draftSpeakerVoice: IDraftSpeakerVoice
  readonly originalSpeakerVoiceState: IDraftSpeakerVoice
  readonly fileToUpload: IFileToUpload | null
  setFileToUpload(file: IFileToUpload | null): void
  handleDraftChange(updatedFields: Partial<IDraftSpeakerVoice>): void
}

const SpeakerVoiceThumbnail: React.FC<SpeakerVoiceThumbnailProps> = props => {
  const {
    draftSpeakerVoice,
    fileToUpload,
  } = props

  const {
    id,
    name,
    color,
    thumbnailUrl: url,
  } = draftSpeakerVoice

  const {
    thumbnailUrl: originalUrl,
  } = props.originalSpeakerVoiceState

  const [showPen, setShowPen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fileToUploadRef = useRef<IFileToUpload | null>(null)

  const isPlaceholder = !url
  const uploading = fileToUpload?.hasStartedUpload && !fileToUpload?.hasCompletedUpload

  useEffect(() => {
    if (!fileToUpload) { return }

    const previousSpeakerVoiceThumbnailFile = fileToUploadRef.current
    fileToUploadRef.current = fileToUpload

    const replaceThumbnail = async () => {
      if (previousSpeakerVoiceThumbnailFile) {
        if (previousSpeakerVoiceThumbnailFile.url) {
          const remover = new S3Remover({
            fileUrl: previousSpeakerVoiceThumbnailFile.url,
          })
          void remover.call().catch(() => undefined)
        } else if (previousSpeakerVoiceThumbnailFile.uploadingRequest) {
          const uploadAborted = await abortUpload(previousSpeakerVoiceThumbnailFile.uploadingRequest)
          if (!uploadAborted) { return }
        }
      }

      if (fileToUploadRef.current !== fileToUpload) { return }

      if (!fileToUpload.hasStartedUpload) {
        const uploader = new AvatarUploader(fileToUpload.file, {
          bucketName: `${window.Checksub?.s3Bucket || window.EditorSources?.s3Bucket}/speaker_voice/${id}`,
          uploadAvatarCallback: onUploadCompleted,
        })

        props.setFileToUpload({
          ...fileToUpload,
          hasStartedUpload: true,
          uploadingRequest: uploader.call(),
        })
      }
    }

    void replaceThumbnail()
  }, [fileToUpload])

  useEffect(() => {
    if (uploading && showPen) {
      hidePen()
    }
  }, [uploading])

  const onUploadCompleted = (params: IUploadedAvatar) => {
    if (!fileToUploadRef.current) { return }

    props.setFileToUpload({
      ...fileToUploadRef.current,
      hasCompletedUpload: true,
      url: params.url,
    })

    const replaceInDraft = () => {
      props.handleDraftChange({ thumbnailUrl: params.url })
    }

    if (url && url !== originalUrl) {
      const remover = new S3Remover({
        fileUrl: url,
      })
      remover.call().then(replaceInDraft).catch(() => undefined)
    } else {
      replaceInDraft()
    }
  }

  const onClick = () => {
    if (uploading) { return }

    fileInputRef.current?.click()
  }

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const resizedFile = await resizeImage(file, 150)

    props.setFileToUpload({
      file: resizedFile,
      hasStartedUpload: false,
      hasCompletedUpload: false,
    })
  }

  const renderPen = () => {
    if (uploading) { return }

    setShowPen(true)
  }

  const hidePen = () => {
    setShowPen(false)
  }

  return (
    <div
      className={classnames(
        'SpeakerVoiceThumbnail',
        isPlaceholder
          ? 'SpeakerVoiceThumbnail__content-placeholder'
          : 'SpeakerVoiceThumbnail__content',
        { 'is-disabled': uploading }
      )}
      style={isPlaceholder ? { backgroundColor: color } : undefined}
      onMouseEnter={renderPen}
      onMouseLeave={hidePen}
      onClick={onClick}
    >
      {isPlaceholder ? (
        showPen ? (
          <Pen />
        ) : (
          <span className="SpeakerVoiceThumbnail__content-initial">
            {name?.charAt(0) ?? ''}
          </span>
        )
      ) : (
        <React.Fragment>
          <img
            src={url}
            alt={'voice thumbnail'}
            className="SpeakerVoiceThumbnail__content-image"
          />
          {showPen && (
            <div className="SpeakerVoiceThumbnail__content-overlay">
              <Pen />
            </div>
          )}
        </React.Fragment>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={onChange}
      />
    </div>
  )
}

export default SpeakerVoiceThumbnail
